import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import styles from './ReviewDialog.module.css';

export interface IReviewDialogProps {
  onClose: () => void;
  title: string;
  /** The reject reason's placeholder — also its accessible name. */
  reasonPlaceholder: string;
  /** Shown when `onReject` fails; the dialog and the typed reason stay. */
  rejectError: string;
  /** Shown when an awaited `onApprove` fails; the dialog stays. */
  approveError?: string;
  /** A promise is awaited like a reject: the dialog closes once it resolves,
   *  or stays to show `approveError`. No promise (an optimistic write that
   *  reports its own failure) closes at once. */
  onApprove: () => Promise<unknown> | void;
  /** Reject needs a reason; the dialog closes once the promise resolves.
   *  `null` while the caller can't act on a reject yet (its data is loading). */
  onReject: ((reason: string) => Promise<void>) | null;
}

/**
 * A manager's review of something submitted for it — an issue, an area (issue
 * and area review stage specs): Approve, or Reject with a reason. Mount it only
 * while something is under review, so every review starts blank.
 *
 * The reason is required, so Reject is disabled while it is empty, being sent,
 * or the caller's data is loading — states the reviewer leaves by typing or
 * waiting (role-permissions ruling 8). While a reject is being sent the dialog
 * can't be dismissed: it closes on success, or stays to show the failure. An
 * awaited approve behaves the same way.
 */
export function ReviewDialog({
  onClose,
  title,
  reasonPlaceholder,
  rejectError,
  approveError,
  onApprove,
  onReject,
}: IReviewDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [state, setState] = useState<'idle' | 'pending' | 'approveFailed' | 'rejectFailed'>('idle');
  const trimmed = reason.trim();
  const pending = state === 'pending';

  const settle = (write: Promise<unknown>, failed: 'approveFailed' | 'rejectFailed') => {
    setState('pending');
    write.then(onClose, () => setState(failed));
  };

  const approve = () => {
    const write = onApprove();
    if (write) settle(write, 'approveFailed');
    else onClose();
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!pending) onClose();
      }}
      title={title}
      size="sm"
      footer={
        <>
          <Button
            variant="danger"
            size="sm"
            disabled={!trimmed || pending || !onReject}
            onClick={onReject ? () => settle(onReject(trimmed), 'rejectFailed') : undefined}
          >
            {t('common.reject')}
          </Button>
          <Button variant="primary" size="sm" disabled={pending} onClick={approve}>
            {t('common.approve')}
          </Button>
        </>
      }
    >
      <textarea
        className={styles.reason}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={reasonPlaceholder}
        aria-label={reasonPlaceholder}
      />
      {state === 'rejectFailed' && <p className={styles.error}>{rejectError}</p>}
      {state === 'approveFailed' && <p className={styles.error}>{approveError}</p>}
    </Modal>
  );
}
